// A visitor's memory of PQ Tube, kept in their own browser and nowhere else.
//
// NOT IN packs/, for the reason mock_composer.js records; imported by
// packs/application.js. The page half is keyed on [data-tube-local], which
// the tube's playlist and home pages render only for a visitor, so it is
// inert for a signed-in student (whose ticks the server draws) and on every
// other page. TubePlayer.jsx imports the helpers below to write the memory.
//
// Two keys:
//   pq:tube:watched — video ids this browser watched, oldest first, capped;
//   pq:tube:resume  — where it stopped on each video, newest first, capped,
//                     with the seconds played so far (the watched rule's
//                     total), and the line's path and wording for the home
//                     page's "Continue watching" (an ended video is not on it).
//
// Every storage call is in try/catch, as in finder_nudge.js: a browser that
// refuses storage (private mode, a full quota) simply remembers nothing.

const WATCHED_KEY = "pq:tube:watched";
const RESUME_KEY = "pq:tube:resume";
const WATCHED_CAP = 500;
const RESUME_CAP = 12;
const CONTINUE_SHOWN = 4;

function readList(key) {
  try {
    const value = JSON.parse(window.localStorage.getItem(key) || "[]");
    return Array.isArray(value) ? value : [];
  } catch (_error) {
    return [];
  }
}

function writeList(key, list) {
  try {
    window.localStorage.setItem(key, JSON.stringify(list));
  } catch (_error) {
    // Storage refused; nothing is remembered.
  }
}

export function rememberWatched(videoId) {
  if (!videoId) return;
  const list = readList(WATCHED_KEY).filter((id) => id !== videoId);
  list.push(videoId);
  writeList(WATCHED_KEY, list.slice(-WATCHED_CAP));
}

export function resumeFor(videoId) {
  return readList(RESUME_KEY).find((entry) => entry && entry.videoId === videoId) || null;
}

export function rememberResume(entry) {
  if (!entry || !entry.videoId) return;
  const rest = readList(RESUME_KEY).filter((e) => e && e.videoId !== entry.videoId);
  writeList(RESUME_KEY, [entry, ...rest].slice(0, RESUME_CAP));
}

export function forgetResume(videoId) {
  const list = readList(RESUME_KEY);
  const rest = list.filter((e) => e && e.videoId !== videoId);
  if (rest.length !== list.length) writeList(RESUME_KEY, rest);
}

// A playlist row lists its videos' ids; the tick is already in the row,
// hidden, with the page's own words.
function tickRows(root) {
  const watched = new Set(readList(WATCHED_KEY));
  if (watched.size === 0) return;
  root.querySelectorAll("[data-tube-videos]").forEach((row) => {
    const ids = (row.getAttribute("data-tube-videos") || "").split(" ").filter(Boolean);
    if (!ids.some((id) => watched.has(id))) return;
    const tick = row.querySelector("[data-tube-watched]");
    if (tick) tick.hidden = false;
  });
}

// Only a path on this site is followed, so a tampered entry cannot link out.
function fillContinue(root) {
  const section = root.querySelector("[data-tube-continue]");
  const list = section && section.querySelector("[data-tube-continue-list]");
  if (!list) return;
  const entries = readList(RESUME_KEY)
    .filter((e) => e && !e.ended && typeof e.path === "string" && /^\/(?!\/)/.test(e.path) && e.line)
    .slice(0, CONTINUE_SHOWN);
  if (entries.length === 0) return;
  entries.forEach((entry) => {
    const card = document.createElement("a");
    card.className = "tube-video";
    card.href = entry.path;

    // The thumbnail is YouTube's own, hotlinked by the video's id, which is
    // checked to be an id before it goes into a URL.
    const thumb = document.createElement("span");
    thumb.className = "tube-thumb";
    if (typeof entry.videoId === "string" && /^[\w-]{6,20}$/.test(entry.videoId)) {
      const img = document.createElement("img");
      img.src = `https://i.ytimg.com/vi/${entry.videoId}/mqdefault.jpg`;
      img.alt = "";
      img.loading = "lazy";
      thumb.appendChild(img);
    }
    card.appendChild(thumb);

    const title = document.createElement("span");
    title.className = "tube-video__title";
    title.textContent = String(entry.line);
    card.appendChild(title);
    if (entry.course) {
      const code = document.createElement("span");
      code.className = "tube-video__meta";
      code.textContent = String(entry.course);
      card.appendChild(code);
    }
    list.appendChild(card);
  });
  section.hidden = false;
}

// "Not watched yet" needs the script, so the button ships hidden. It hides
// the rows whose thumb wears a SHOWN watched bar: server-drawn for a signed-in
// student, revealed by tickRows for a visitor.
function setupUnwatched() {
  document.querySelectorAll("[data-tube-unwatched]").forEach((button) => {
    button.hidden = false;
    button.addEventListener("click", () => {
      const on = button.getAttribute("aria-pressed") !== "true";
      button.setAttribute("aria-pressed", String(on));
      document.querySelectorAll(".tube-row").forEach((row) => {
        const seen = row.querySelector(".tube-thumb__watched:not([hidden])");
        row.hidden = on && !!seen;
      });
      document.querySelectorAll(".tube-run").forEach((run) => {
        const rows = run.querySelectorAll(".tube-row");
        run.hidden = on && rows.length > 0 && Array.from(rows).every((row) => row.hidden);
      });
    });
  });
}

function setup() {
  setupUnwatched();
  const root = document.querySelector("[data-tube-local]");
  if (!root) return;
  tickRows(root);
  fillContinue(root);
}

// The same guard every module in this pack carries — see mock_request_wizard.js.
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", setup);
} else {
  setup();
}
